/**
 * O parser — de lista de símbolos para árvore, com precedência.
 *
 * Descida recursiva, um nível de função por faixa de precedência. É o desenho mais longo de
 * escrever e o mais curto de LER, e num avaliador que decide número de dinheiro isso vale mais
 * que economia de linhas.
 *
 * Precedência, da mais fraca para a mais forte — a mesma da planilha, porque é a que o usuário
 * já tem na cabeça:
 *
 *     comparação   ==  !=  >  <  >=  <=      (não associativa: `a < b < c` é recusado)
 *     aditiva      +  -                       (esquerda)
 *     multiplicativa  *  /                    (esquerda)
 *     unário       -x
 *     primário     número · texto · prop(…) · f(…) · ( … )
 *
 * ⚠️ **`a < b < c` é erro, não `(a<b)<c`.** Em JavaScript aquilo avalia para
 * `true < c` → `1 < c` e devolve um booleano que ninguém pediu. Numa fórmula de "está dentro
 * da faixa?" o resultado seria plausível e errado — o pior formato de defeito. A mensagem
 * sugere `and(a < b, b < c)`, que é o que a pessoa queria.
 */

import { erro } from './erros';
import type { No, OperadorBinario } from './ast';
import { tokenizar, type Token } from './tokenizer';

/** O que o parser precisa saber sobre as colunas — e nada além disso. */
export interface CatalogoDeColunas {
  /** `undefined` quando não existe coluna com esse nome. */
  idPorNome(nome: string): string | undefined;
  /** Usado só na mensagem de erro, para sugerir o que a pessoa provavelmente quis. */
  nomes(): string[];
}

const COMPARACAO: OperadorBinario[] = ['==', '!=', '>=', '<=', '>', '<'];

/**
 * Limite de aninhamento.
 *
 * Não é preciosismo: o avaliador é recursivo, e uma expressão colada de outro lugar com
 * centenas de parênteses estoura a pilha do navegador — o que aparece como a **aba travando**,
 * não como erro de fórmula. Recusar em 64 níveis devolve uma mensagem; não recusar devolve um
 * `RangeError` que ninguém liga à célula que o causou.
 */
const PROFUNDIDADE_MAXIMA = 64;

class Analisador {
  private i = 0;
  private profundidade = 0;

  private readonly tokens: Token[];
  private readonly catalogo: CatalogoDeColunas;

  // ⚠️ Campos escritos por extenso em vez de `constructor(private readonly …)`. A forma curta
  // é açúcar do TypeScript que EMITE código (ela cria a atribuição), e por isso não sobrevive
  // à remoção-só-de-tipos do `node --experimental-strip-types` — que é como a suíte deste
  // avaliador roda nesta estação, sem build do Affine. Duas linhas a mais valem a suíte que
  // responde em milissegundos.
  constructor(tokens: Token[], catalogo: CatalogoDeColunas) {
    this.tokens = tokens;
    this.catalogo = catalogo;
  }

  private atual(): Token {
    return this.tokens[this.i];
  }

  private consumir(): Token {
    return this.tokens[this.i++];
  }

  private esperar(tipo: Token['tipo'], oQue: string): Token {
    const t = this.atual();
    if (t.tipo !== tipo) {
      throw erro('sintaxe', `esperava ${oQue} e encontrei "${t.valor || 'fim da expressão'}"`,
        t.posicao);
    }
    return this.consumir();
  }

  private entrar(): void {
    this.profundidade += 1;
    if (this.profundidade > PROFUNDIDADE_MAXIMA) {
      throw erro('profundidade',
        `expressão aninhada além de ${PROFUNDIDADE_MAXIMA} níveis`, this.atual().posicao);
    }
  }

  private sair(): void {
    this.profundidade -= 1;
  }

  analisar(): No {
    const no = this.comparacao();
    const sobra = this.atual();
    if (sobra.tipo !== 'fim') {
      throw erro('sintaxe',
        `sobrou "${sobra.valor}" depois do fim da expressão`, sobra.posicao);
    }
    return no;
  }

  private comparacao(): No {
    const esquerda = this.aditiva();
    const t = this.atual();
    if (t.tipo !== 'operador' || !COMPARACAO.includes(t.valor as OperadorBinario)) {
      return esquerda;
    }
    this.consumir();
    const direita = this.aditiva();

    const seguinte = this.atual();
    if (seguinte.tipo === 'operador'
      && COMPARACAO.includes(seguinte.valor as OperadorBinario)) {
      throw erro('sintaxe',
        'comparação encadeada não é aceita — use and(a < b, b < c)', seguinte.posicao);
    }

    return { tipo: 'binario', operador: t.valor as OperadorBinario, esquerda, direita };
  }

  private aditiva(): No {
    let no = this.multiplicativa();
    for (;;) {
      const t = this.atual();
      if (t.tipo !== 'operador' || (t.valor !== '+' && t.valor !== '-')) return no;
      this.consumir();
      no = {
        tipo: 'binario',
        operador: t.valor as OperadorBinario,
        esquerda: no,
        direita: this.multiplicativa(),
      };
    }
  }

  private multiplicativa(): No {
    let no = this.unaria();
    for (;;) {
      const t = this.atual();
      if (t.tipo !== 'operador' || (t.valor !== '*' && t.valor !== '/')) return no;
      this.consumir();
      no = {
        tipo: 'binario',
        operador: t.valor as OperadorBinario,
        esquerda: no,
        direita: this.unaria(),
      };
    }
  }

  private unaria(): No {
    const t = this.atual();
    if (t.tipo === 'operador' && t.valor === '-') {
      this.consumir();
      return { tipo: 'unario', operador: '-', operando: this.unaria() };
    }
    // `+5` é aceito e descartado: quem escreve `+5` quer `5`, e recusar seria rigor sem ganho.
    if (t.tipo === 'operador' && t.valor === '+') {
      this.consumir();
      return this.unaria();
    }
    return this.primaria();
  }

  private primaria(): No {
    this.entrar();
    try {
      const t = this.consumir();

      if (t.tipo === 'numero') return { tipo: 'numero', valor: t.valor as number };
      if (t.tipo === 'texto') return { tipo: 'texto', valor: t.valor as string };

      if (t.tipo === 'abre') {
        const dentro = this.comparacao();
        this.esperar('fecha', '")"');
        return dentro;
      }

      if (t.tipo === 'identificador') {
        const nome = t.valor as string;

        if (nome === 'true') return { tipo: 'booleano', valor: true };
        if (nome === 'false') return { tipo: 'booleano', valor: false };

        // Só existem chamadas: `prop` e as funções. Um identificador solto não é variável —
        // é quase sempre uma coluna escrita sem `prop(...)`, e a mensagem diz isso.
        if (this.atual().tipo !== 'abre') {
          const talvezColuna = this.catalogo.idPorNome(nome) !== undefined;
          throw erro('sintaxe',
            talvezColuna
              ? `"${nome}" é uma coluna — escreva prop("${nome}")`
              : `"${nome}" não é função nem valor. Se for coluna, escreva prop("${nome}")`,
            t.posicao);
        }
        this.consumir(); // o "("

        if (nome === 'prop') return this.referenciaDeColuna(t.posicao);

        const argumentos: No[] = [];
        if (this.atual().tipo !== 'fecha') {
          for (;;) {
            argumentos.push(this.comparacao());
            if (this.atual().tipo === 'virgula') { this.consumir(); continue; }
            break;
          }
        }
        this.esperar('fecha', `")" para fechar ${nome}(`);
        return { tipo: 'chamada', nome, argumentos, posicao: t.posicao };
      }

      throw erro('sintaxe',
        `esperava um valor e encontrei "${t.valor || 'fim da expressão'}"`, t.posicao);
    } finally {
      this.sair();
    }
  }

  /**
   * `prop("Nome")` → `{tipo:'prop', propertyId}`.
   *
   * A resolução nome → id acontece **aqui e só aqui**. O erro de coluna ausente é levantado no
   * momento em que a fórmula é ESCRITA, não quando é avaliada — o editor mostra na hora,
   * enquanto a pessoa ainda sabe o que quis dizer.
   */
  private referenciaDeColuna(posicao: number): No {
    const t = this.atual();
    if (t.tipo !== 'texto') {
      throw erro('sintaxe',
        'prop() espera o nome da coluna entre aspas — ex.: prop("Valor")', t.posicao);
    }
    this.consumir();
    const nome = t.valor as string;
    this.esperar('fecha', '")" para fechar prop(');

    const id = this.catalogo.idPorNome(nome);
    if (id === undefined) {
      const disponiveis = this.catalogo.nomes();
      const sugestao = disponiveis.length
        ? ` Colunas disponíveis: ${disponiveis.map(n => `"${n}"`).join(', ')}`
        : '';
      throw erro('coluna-ausente', `não existe coluna chamada "${nome}".${sugestao}`, posicao);
    }
    return { tipo: 'prop', propertyId: id, nomeOriginal: nome };
  }
}

export function analisar(expressao: string, catalogo: CatalogoDeColunas): No {
  return new Analisador(tokenizar(expressao), catalogo).analisar();
}
